import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faLaptop, 
  faShieldAlt,
  faSimCard,
  faNetworkWired,
  faClipboardList,
  faChartLine,
  faPlug
} from '@fortawesome/free-solid-svg-icons';
import NetworkDiagram from './NetworkDiagram';
import './RemoteAccess.scss';

/**
 * RemoteAccess component - Displays information about MET Connect®'s remote access solutions
 * 
 * @returns {JSX.Element} The RemoteAccess component
 */
const RemoteAccess = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  /**
   * Handle contact button click - navigate to homepage and scroll to contact section
   */
  const handleContactClick = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  // Features of the remote access solution with their descriptions
  const features = [
    {
      title: "Multiple User Accounts",
      description: "Simple firewall rules can allow some users restricted access to only certain devices whilst Engineer accounts can have complete unrestricted network access.",
      icon: faUsers
    },
    {
      title: "Supported on Multiple Platforms",
      description: "Built-in support with every common OS – No client software to install.",
      icon: faLaptop
    },
    {
      title: "Every Major VPN Protocol Supported",
      description: "L2TP/IPSec, OpenVPN, Microsoft SSTP, L2TPv3/IPSec, EtherIP/IPSec",
      icon: faShieldAlt
    },
    {
      title: "No Special SIM Card Provisioning",
      description: "Any off-the-shelf data plan from any mobile provider will work. No requirement for custom APNs, Public IP or Static IP Addresses.",
      icon: faSimCard
    },
    {
      title: "Layer 2 – Ethernet Connection",
      description: "Your remote computer behaves as if it's plugged in directly to your network switch on-site. No requirement for default gateways programmed in all remote network devices.",
      icon: faNetworkWired
    },
    {
      title: "Audit Trail",
      description: "Security logs show who logged in when. Packet logs can even be activated which captures exactly what each user did on the network.",
      icon: faClipboardList
    },
    {
      title: "Network Monitoring",
      description: "The MET Connect® comprehensive Remote Monitoring Platform can be setup to monitor the devices on your network and alert you to any possible issues.",
      icon: faChartLine
    },
    {
      title: "Ease of Use",
      description: "Very simple to setup, deploy and manage. No complex network configuration needed whatsoever. We can configure your MODEM, you simply plug it in and switch it on!",
      icon: faPlug
    }
  ];

  return (
    <div className="remote-access-page">
      <div className="hero-section" style={{
        backgroundImage: `linear-gradient(rgba(36, 36, 36, 0.5), rgba(36, 36, 36, 0.7)), url(${process.env.PUBLIC_URL}/images/network-pattern-bg.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Remote Access Solution
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                The secure remote access solution you need for accessing and monitoring your remotely located control system network.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faNetworkWired} className="feature-icon" />
                  <span>Layer 2 Ethernet Connection</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                  <span>Enterprise-Grade Security</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="solution-overview">
        <Container>
          <Row>
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="content-block">
                <h2>The MET Connect<sup>®</sup> Solution</h2>
                <p>
                  The MET Connect® Service is the secure remote access solution you need for accessing and monitoring 
                  your remotely located control system network. No complex dynamic-DNS setup or static IP address 
                  provisioning with your data provider.
                </p>
                <p>
                  By linking your remote 4G MODEMs to the MET Connect® Service, you achieve a low-cost, secure, 
                  enterprise-scalable remote access solution.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="image-block">
                <NetworkDiagram />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="features-section">
        <Container>
          <h2 className="text-center mb-5">Key Features</h2>
          <Row>
            {features.map((feature, index) => (
              <Col lg={6} xl={4} key={index} className="mb-4">
                <Card className="feature-card h-100">
                  <Card.Body>
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cta-section">
        <Container className="text-center">
          <h3>Want more information?</h3>
          <p>Please contact us and we will be happy to help with your network requirements.</p>
          <Button className="btn btn-primary btn-lg" onClick={handleContactClick}>
            Contact Us Today
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default RemoteAccess;